/**
 * Wiki-links "[[page]]": atomic inline node referencing pages by id.
 *
 * - Reference is by `pageId`, so renaming a page does not break the
 *   link: NodeView always displays the current title (reactive lookup
 *   via `getPages` + registration of refresh callbacks).
 * - `label` stored in JSON is a fallback (deleted or not-yet-synced page)
 *   and what indexes backlink LIKE search.
 * - Typing "[[" opens autocompletion; if there is no exact match it
 *   offers creating a page with that title.
 */
import { type Editor, type Range, Node as TiptapNode } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionOptions } from "@tiptap/suggestion";

const MAX_SUGGESTIONS = 8;

interface WikiLinkPageRef {
  id: string;
  title: string | null;
  icon: string | null;
}

export interface WikiLinkSuggestionItem {
  /** null → "Create page …" option */
  pageId: string | null;
  title: string;
  icon: string | null;
  isCreate: boolean;
}

interface WikiLinkConfig {
  /** Current list of visible pages (queried on each use). */
  getPages: () => WikiLinkPageRef[];
  /** Navigates to a page when clicking the link. */
  navigate: (pageId: string) => void;
  /** Creates a page with that title; returns its id (or null on failure). */
  createPage: (title: string) => Promise<string | null>;
  /**
   * Registers a callback to refresh displayed title when pages
   * change. Returns unregister function.
   */
  registerRefresh: (callback: () => void) => () => void;
  untitledLabel: string;
  render: SuggestionOptions<WikiLinkSuggestionItem, WikiLinkSuggestionItem>["render"];
}

function insertWikiLink(editor: Editor, range: Range, pageId: string, label: string): void {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent([
      { type: "wikiLink", attrs: { pageId, label } },
      { type: "text", text: " " },
    ])
    .run();
}

export function createWikiLinkExtension(config: WikiLinkConfig) {
  return TiptapNode.create({
    name: "wikiLink",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,

    addAttributes() {
      return {
        pageId: { default: null },
        label: { default: "" },
      };
    },

    parseHTML() {
      return [
        {
          tag: "span[data-wiki-link-id]",
          getAttrs: (element) => ({
            pageId: element.dataset.wikiLinkId,
            label: element.textContent ?? "",
          }),
        },
      ];
    },

    renderHTML({ node }) {
      const label = typeof node.attrs.label === "string" ? node.attrs.label : "";
      const pageId = typeof node.attrs.pageId === "string" ? node.attrs.pageId : "";
      return ["span", { "data-wiki-link-id": pageId, class: "wiki-link" }, label];
    },

    addNodeView() {
      return ({ node }) => {
        const dom = document.createElement("span");
        dom.className = "wiki-link";

        const pageId = typeof node.attrs.pageId === "string" ? node.attrs.pageId : null;
        const storedLabel = typeof node.attrs.label === "string" ? node.attrs.label : "";

        const currentTitle = (): string => {
          const page = pageId ? config.getPages().find((p) => p.id === pageId) : undefined;
          if (page) {
            const title = page.title?.trim();
            const icon = page.icon ? `${page.icon} ` : "";
            return `${icon}${title && title.length > 0 ? title : config.untitledLabel}`;
          }
          return storedLabel.length > 0 ? storedLabel : config.untitledLabel;
        };

        dom.textContent = currentTitle();
        dom.addEventListener("click", (event) => {
          event.preventDefault();
          if (pageId) {
            config.navigate(pageId);
          }
        });

        const unregister = config.registerRefresh(() => {
          dom.textContent = currentTitle();
        });

        return {
          dom,
          destroy: () => {
            unregister();
          },
        };
      };
    },

    addProseMirrorPlugins() {
      return [
        Suggestion<WikiLinkSuggestionItem, WikiLinkSuggestionItem>({
          editor: this.editor,
          char: "[[",
          pluginKey: new PluginKey("wiki-link-suggestion"),
          allowSpaces: true,
          command: ({ editor, range, props }) => {
            if (props.pageId) {
              insertWikiLink(editor, range, props.pageId, props.title);
              return;
            }
            // Create page on the fly and link it
            void (async () => {
              try {
                const id = await config.createPage(props.title);
                if (id) {
                  insertWikiLink(editor, range, id, props.title);
                }
              } catch (err: unknown) {
                console.error(
                  "[notes] Error creating page from wiki-link:",
                  err instanceof Error ? err.message : String(err),
                );
              }
            })();
          },
          items: ({ query }) => {
            const q = query.trim().toLowerCase();
            const matches = config
              .getPages()
              .filter((page) => {
                if (q.length === 0) {
                  return true;
                }
                return (page.title ?? "").toLowerCase().includes(q);
              })
              .slice(0, MAX_SUGGESTIONS)
              .map<WikiLinkSuggestionItem>((page) => ({
                pageId: page.id,
                // Intentional `||`: empty title after trim ("") should fall back to
                // placeholder; `??` would only cover null/undefined and leave "".
                title: page.title?.trim() ?? config.untitledLabel,
                icon: page.icon,
                isCreate: false,
              }));

            const trimmed = query.trim();
            const hasExact = matches.some(
              (item) => item.title.toLowerCase() === trimmed.toLowerCase(),
            );
            if (trimmed.length > 0 && !hasExact) {
              matches.push({ pageId: null, title: trimmed, icon: null, isCreate: true });
            }
            return matches;
          },
          render: config.render,
        }),
      ];
    },
  });
}
